import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { fr, nl, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

/**
 * Small live clock badge shown in the navbar next to the "S'inscrire" button.
 * Only HH:MM in Brussels time, with a subtle pulsing dot to signal it is live.
 * On hover: a tooltip reveals the full weekday + date + Brussels timezone label.
 *
 * Props:
 *  - variant: "desktop" (default) or "mobile" (larger, block-style for the burger menu)
 */
export default function LiveClockBadge({ variant = 'desktop' }) {
  const { i18n, t } = useTranslation();
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 15000);
    return () => clearInterval(id);
  }, []);

  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  const dateLocale = ({ fr, nl, en: enUS }[(i18n.language || 'fr').slice(0, 2)]) || fr;
  const fullDate = format(now, 'EEEE d MMMM yyyy', { locale: dateLocale });

  if (variant === 'mobile') {
    // Larger block-style version for the mobile burger menu
    return (
      <div
        className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-white/8 border border-white/15 text-white"
        data-testid="mobile-live-clock"
      >
        <span className="relative flex h-2 w-2" aria-hidden="true">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C77D63] opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#C77D63]"></span>
        </span>
        <div className="flex flex-col leading-tight">
          <div className="font-mono text-lg font-bold tabular-nums">{hh}:{mm}</div>
          <div className="text-[10px] text-white/70 capitalize">
            {fullDate} · {t('booking.timezoneShort', 'Bruxelles')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/10 border border-white/15 text-white text-xs font-mono tabular-nums cursor-help"
            data-testid="navbar-live-clock"
          >
            <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C77D63] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#C77D63]"></span>
            </span>
            <span>{hh}:{mm}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          className="bg-[#1A3C34] text-white border-[#1A3C34] px-3 py-2"
        >
          <div className="text-xs">
            <div className="font-semibold capitalize">{fullDate}</div>
            <div className="text-white/70 mt-0.5">
              {t('booking.timezoneFull', 'Fuseau : Europe/Bruxelles (CET/CEST)')}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
